<template>
	<div>
		<!-- Title -->
		<CCol md="12" class="mb-4">
			<CRow align-horizontal="end">
				<CButton
					color="primary"
					class="mr-2"
					:disabled="!widgets.length || isSavingSort"
					@click="handleSaveSort"
				>
					<CSpinner
						v-if="isSavingSort"
						color="light"
						size="sm"
						class="mr-2"
					/>
					<CIcon
						v-else
						class="mr-2"
						name="cil-save"
					/>
					Save Sort
				</CButton>

				<CDropdown
					add-menu-classes="import-dropdown-menu"
					class="text-right btn-dropdown"
					color="secondary"
				>
					<CDropdownItem @click="onClickOpenModal(REFS_HIGHLIGHT_BANNER_WIDGET_MODAL, { type: WIDGET_TYPE.highlight })">
						Highlight banner
					</CDropdownItem>
					<hr>
					<CDropdownItem @click="onClickOpenModal(REFS_PRODUCT_WIDGET_MODAL)">
						Product
					</CDropdownItem>
					<CDropdownItem @click="onClickOpenModal(REFS_WYSIWYG_WIDGET_MODAL)">
						Wysiwyg
					</CDropdownItem>
					<CDropdownItem @click="onClickOpenModal(REFS_CUSTOM_HERO_BANNER_WIDGET_MODAL, { type: WIDGET_TYPE.custom_hero })">
						Custom hero banner
					</CDropdownItem>
					<CDropdownItem @click="onClickOpenModal(REFS_HIGHLIGHT_LOB_WIDGET_MODAL, { type: WIDGET_TYPE.highlight_lob_banner })">
						Highlight LOB banner
					</CDropdownItem>
					<CDropdownItem @click="onClickOpenModal(REFS_LOB_WIDGET_MODAL, { type: WIDGET_TYPE.lob })">
						LOB banner
					</CDropdownItem>
					<template #toggler-content>
						<CIcon class="mr-2" name="cil-plus" />Create widget
					</template>
				</CDropdown>
			</CRow>
		</CCol>

		<!-- Filter -->
		<CRow>
			<CCol class="d-flex align-items-start">
				<form
					class="search-form flex-fill"
					@submit.prevent="handleSearch"
				>
					<CInput
						v-model.trim="filter.q"
						type="text"
						placeholder="Search by name"
					>
						<template #prepend-content>
							<button class="btn-search" type="submit">
								<FontAwesomeIcon :icon="['far', 'search']" />
							</button>
						</template>
					</CInput>
				</form>
				<BaseFilterButton
					:is-open="isShowFilter"
					:has-filter="hasFilter"
					class="ml-3"
					data-id="filter-button"
					@onClick="toggleFilter"
				/>
			</CCol>
		</CRow>

		<!-- Filter Content -->
		<CRow>
			<CCol>
				<transition name="fade">
					<div
						v-if="isShowFilter"
						class="bg-gray-100 rounded-sm pt-3 px-3 mb-3"
					>
						<CRow>
							<CCol md="3" class="mb-3">
								<BaseDropDown
									v-model="filter.type"
									:options="WIDGET_TYPE_OPTIONS"
									:allow-empty="false"
									:searchable="false"
									label="name"
									track-by="value"
									placeholder="All type"
									@input="fetchWidgets"
								/>
							</CCol>
							<CCol md="3" class="mb-3">
								<BaseDropDown
									v-model="filter.websiteVisibility"
									:options="VISIBILITY_OPTIONS"
									:allow-empty="false"
									:searchable="false"
									label="name"
									track-by="value"
									placeholder="All website visibility"
									@input="fetchWidgets"
								/>
							</CCol>
							<CCol md="3" class="mb-3">
								<BaseDropDown
									v-model="filter.mobileVisibility"
									:options="VISIBILITY_OPTIONS"
									:allow-empty="false"
									:searchable="false"
									label="name"
									track-by="value"
									placeholder="All mobile visibility"
									@input="fetchWidgets"
								/>
							</CCol>
						</CRow>
					</div>
				</transition>
			</CCol>
		</CRow>
		<CRow>
			<CCol class="mb-4">
				<BaseSearchFormFooter
					:count="widgets.length"
					append-text="lists found"
					@onReset="handleResetFilter"
				/>
			</CCol>
		</CRow>

		<div class="table-container mt-4">
			<BaseTable
				:fields="STATIC_LANDING_PAGE_WIDGET_TABLE_FIELDS"
				:items="widgets"
				:is-loading="isLoading"
				is-draggable
				class="table-custom widget-table-other"
				@drag="handleUpdateOrder"
			>
				<template #no-items-view>
					<div class="empty-table-element">
						<p class="subtitle">
							{{ $t("global.searchNotFound", { field: "Widget" }) }}
						</p>
					</div>
				</template>
				<template #name="{item}">
					<strong>{{ item }}</strong>
				</template>
				<template #linkTo="{item}">
					<a class="link-to" @click="onClickLink(item)">
						{{ item.name || 'Add banner' }}
					</a>
					<div v-if="item.status" class="link-status">
						{{ item.status }}
					</div>
				</template>
				<template #websiteVisibility="{item}">
					<CBadge class="badge-status" :color="item.value">
						{{ item.name }}
					</CBadge>
				</template>
				<template #mobileVisibility="{item}">
					<CBadge class="badge-status" :color="item.value">
						{{ item.name }}
					</CBadge>
				</template>
				<template #edit="{item}">
					<div class="text-left">
						<CButton
							v-if="item.editable"
							class="transparent p-0 btn-edit"
							@click="handleEdit(item.id)"
						>
							<CIcon class="icon-pencil" name="cil-pencil" />
						</CButton>
					</div>
				</template>
			</BaseTable>
		</div>

		<ModalProductWidget
			:ref="REFS_PRODUCT_WIDGET_MODAL"
			@onSubmit="onSubmitModal"
			@onRemove="handleRemove"
		/>
		<ModalWYSIWYGWidget
			:ref="REFS_WYSIWYG_WIDGET_MODAL"
			@onSubmit="onSubmitModal"
			@onRemove="handleRemove"
		/>
		<ModalAddWYSIWYG
			:ref="REFS_ADD_WYSIWYG_MODAL"
			@onSubmit="onSubmitModal"
		/>
		<ModalLOBWidget
			:ref="REFS_LOB_WIDGET_MODAL"
			@onSubmit="onSubmitModal"
			@onRemove="handleRemove"
		/>
		<ModalHighlightLOBWidget
			:ref="REFS_HIGHLIGHT_LOB_WIDGET_MODAL"
			@onSubmit="onSubmitModal"
			@onRemove="handleRemove"
		/>
		<ModalHighlightWidget
			:ref="REFS_HIGHLIGHT_WIDGET_MODAL"
			@onSubmit="onSubmitModal"
			@onRemove="handleRemove"
		/>
		<ModalCustomHeroBannerWidget
			:ref="REFS_CUSTOM_HERO_BANNER_WIDGET_MODAL"
			@onSubmit="onSubmitModal"
			@onRemove="handleRemove"
		/>
		<ModalHighlightBannerWidget
			:ref="REFS_HIGHLIGHT_BANNER_WIDGET_MODAL"
			@onSubmit="onSubmitModal"
			@onRemove="handleRemove"
		/>

		<BaseModalConfirmDelete
			ref="modal-remove"
			:handle-remove="deletePageWidgets.bind(null, editedId)"
			title="Remove this widget?"
			description="By removing this, the widget will be disappeared from the list and linked items will be lost"
			@onSuccess="fetchWidgets"
		/>
	</div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import debounce from 'lodash/debounce';
import {
	STATIC_LANDING_PAGE_WIDGET_TABLE_FIELDS,
	STATIC_LANDING_PAGE_STATUSES,
} from '../enums/staticLandingPage';
import ModalProductWidget from './WidgetsStaticPage/ModalProductWidget.vue';
import ModalWYSIWYGWidget from './WidgetsStaticPage/ModalWYSIWYGWidget.vue';
import ModalAddWYSIWYG from './WidgetsStaticPage/ModalAddWYSIWYG.vue';
import ModalLOBWidget from './WidgetsStaticPage/ModalLOBWidget.vue';
import ModalHighlightLOBWidget from './WidgetsStaticPage/ModalHighlightLOBWidget.vue';
import ModalHighlightWidget from './WidgetsStaticPage/ModalHighlightWidget.vue';
import ModalCustomHeroBannerWidget from './WidgetsStaticPage/ModalCustomHeroBannerWidget.vue';
import ModalHighlightBannerWidget from './WidgetsStaticPage/ModalHighlightBannerWidget.vue';
import { ROUTE_NAME } from '../enums/route';
import BaseModalConfirmDelete from './BaseModalConfirmDelete.vue';
import {
	WIDGET_TYPE,
	WIDGET_VISIBILITY,
} from '../enums/widgets';

const WIDGET_ROUTES = {
	[WIDGET_TYPE.hero]: 'BannerHeroList',
	[WIDGET_TYPE.custom_hero]: 'BannerCustomHeroList',
	[WIDGET_TYPE.product_category]: 'BannerProductCategory',
	[WIDGET_TYPE.lob]: 'BannerLobList',
	[WIDGET_TYPE.highlight_lob]: 'BannerHighlightLobList',
	[WIDGET_TYPE.highlight]: 'BannerHighlightList',
	[WIDGET_TYPE.small]: 'BannerSmallList',
	[WIDGET_TYPE.shortcut]: 'BannerShortcutList',
	[WIDGET_TYPE.content]: 'MicrositeBannerContentList',
	[WIDGET_TYPE.brandBanner]: 'BrandBannerEdit',
	[WIDGET_TYPE.productHighlight]: 'ProductHighlightEdit',
	[WIDGET_TYPE.shop_by_brand]: 'BannerShopByBrandList',
	[WIDGET_TYPE.highlightContentBlock]: ROUTE_NAME.CONTENT_BLOCK_HIGHLIGHT_LIST,
	[WIDGET_TYPE.stripeContentBlock]: ROUTE_NAME.CONTENT_BLOCK_STRIPE_LIST,
	[WIDGET_TYPE.textWithImageContentBlock]: ROUTE_NAME.CONTENT_BLOCK_TEXT_WITH_IMAGE_LIST,
};

const REFS_PRODUCT_WIDGET_MODAL = 'modal-product-widget';
const REFS_WYSIWYG_WIDGET_MODAL = 'modal-wysiwyg-widget';
const REFS_ADD_WYSIWYG_MODAL = 'modal-add-wysiwyg';
const REFS_LOB_WIDGET_MODAL = 'modal-lob-widget';
const REFS_HIGHLIGHT_LOB_WIDGET_MODAL = 'modal-highlight-lob-widget';
const REFS_HIGHLIGHT_WIDGET_MODAL = 'modal-highlight-widget';
const REFS_CUSTOM_HERO_BANNER_WIDGET_MODAL = 'modal-custom-hero-banner-widget';
const REFS_HIGHLIGHT_BANNER_WIDGET_MODAL = 'modal-highlight-banner-widget';

export default {
	name: 'StaticLandingPageStepWidgetContent',

	components: {
		ModalProductWidget,
		ModalWYSIWYGWidget,
		ModalAddWYSIWYG,
		ModalLOBWidget,
		ModalHighlightLOBWidget,
		ModalHighlightWidget,
		ModalCustomHeroBannerWidget,
		ModalHighlightBannerWidget,
		BaseModalConfirmDelete,
	},

	props: {
		defaultData: {
			type: Object,
			default: null,
		},
	},

	data() {
		return {
			WIDGET_TYPE,
			WIDGET_VISIBILITY,
			STATIC_LANDING_PAGE_WIDGET_TABLE_FIELDS,
			STATIC_LANDING_PAGE_STATUSES,
			REFS_PRODUCT_WIDGET_MODAL,
			REFS_WYSIWYG_WIDGET_MODAL,
			REFS_ADD_WYSIWYG_MODAL,
			REFS_LOB_WIDGET_MODAL,
			REFS_HIGHLIGHT_LOB_WIDGET_MODAL,
			REFS_HIGHLIGHT_WIDGET_MODAL,
			REFS_CUSTOM_HERO_BANNER_WIDGET_MODAL,
			REFS_HIGHLIGHT_BANNER_WIDGET_MODAL,
			isLoading: false,
			isSavingSort: false,
			widgets: [],
			filter: {
				q: '',
				type: { name: 'All type', value: null },
				websiteVisibility: { name: 'All visibility', value: null },
				mobileVisibility: { name: 'All visibility', value: null },
			},
			VISIBILITY_OPTIONS: [
				{ name: 'All visibility', value: null },
				{ name: WIDGET_VISIBILITY.true?.name || 'On', value: 1 },
				{ name: WIDGET_VISIBILITY.false?.name || 'Off', value: 0 },
			],
			WIDGET_TYPE_OPTIONS: [
				{ name: 'All type', value: null },
				{ name: 'Highlight banner', value: WIDGET_TYPE.highlight },
				{ name: 'Product', value: WIDGET_TYPE.product },
				{ name: 'Wysiwyg', value: WIDGET_TYPE.wysiwyg },
				{ name: 'Custom hero banner', value: WIDGET_TYPE.custom_hero },
				{ name: 'Highlight LOB banner', value: WIDGET_TYPE.highlight_lob_banner },
				{ name: 'LOB banner', value: WIDGET_TYPE.lob },
			],
			isShowFilter: false,
			isEditMode: true,
			editedId: null,
			widgetRefs: null,
			modalTitle: '',
		};
	},

	computed: {
		...mapState('pages', {
			createWidgetState: 'createWidget',
			editWidgetState: 'editWidget',
			listWidget: 'listWidget',
		}),
		hasFilter() {
			return !!this.filter.q ||
				(this.filter.type && this.filter.type.value !== null) ||
				(this.filter.websiteVisibility && this.filter.websiteVisibility.value !== null) ||
				(this.filter.mobileVisibility && this.filter.mobileVisibility.value !== null);
		},
	},

	watch: {
		listWidget: {
			deep: true,
			immediate: true,
			handler(listWidget) {
				this.widgets = listWidget.data || [];
			},
		},
	},

	created() {
		this.fetchWidgets();
	},

	methods: {
		...mapActions({
			getPageWidgetList: 'pages/getPageWidgetList',
			createPageWidgets: 'pages/createPageWidgets',
			updatePageWidgets: 'pages/updatePageWidgets',
			deletePageWidgets: 'pages/deletePageWidgets',
			sortPageWidgets: 'pages/sortPageWidgets',
			showToast: 'toast/showToast',
		}),
		async fetchWidgets() {
			this.isLoading = true;
			try {
				const params = {
					per_page: 'all',
					q: this.filter.q,
					type: this.filter.type?.value,
					is_active: this.filter.websiteVisibility?.value,
					is_mobile_active: this.filter.mobileVisibility?.value,
					page_id: this.$route.params.id,
				};
				await this.getPageWidgetList(params);
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error('Error fetching widgets:', error);
			} finally {
				this.isLoading = false;
			}
		},

		handleSearch: debounce(function handleSearch() {
			this.fetchWidgets();
		}, 500),

		handleFetch() {
			this.fetchWidgets();
		},

		handleUpdateOrder(sortedList) {
			this.widgets = sortedList;
		},

		async handleSaveSort() {
			this.isSavingSort = true;
			try {
				const params = {
					page_id: this.$route.params.id,
					page_widget_ids: this.widgets.map((item) => item.id),
				};
				await this.sortPageWidgets(params);

				this.showToast({
					header: 'Success',
					type: 'success',
					content: 'Saved widget order successfully.',
				});
				await this.fetchWidgets();
			} catch (error) {
				const { data } = error.response || {};
				this.showToast({
					header: 'Error',
					type: 'danger',
					content: data?.message || 'Failed to save widget order.',
				});
			} finally {
				this.isSavingSort = false;
			}
		},

		toggleFilter() {
			this.isShowFilter = !this.isShowFilter;
		},

		handleResetFilter() {
			this.filter = {
				q: '',
				type: this.WIDGET_TYPE_OPTIONS[0],
				websiteVisibility: this.VISIBILITY_OPTIONS[0],
				mobileVisibility: this.VISIBILITY_OPTIONS[0],
			};
			this.fetchWidgets();
		},

		handleEdit(id) {
			const editData = this.widgets.find((item) => item.id === id);
			if (!editData) return;

			if (editData.type === WIDGET_TYPE.wysiwyg) {
				this.onClickOpenModal(REFS_WYSIWYG_WIDGET_MODAL, editData);
			} else if (editData.type === WIDGET_TYPE.product || editData.type === WIDGET_TYPE.products) {
				this.onClickOpenModal(REFS_PRODUCT_WIDGET_MODAL, editData);
			} else if (editData.type === WIDGET_TYPE.highlight) {
				this.onClickOpenModal(REFS_HIGHLIGHT_BANNER_WIDGET_MODAL, editData);
			} else if (editData.type === WIDGET_TYPE.custom_hero) {
				this.onClickOpenModal(REFS_CUSTOM_HERO_BANNER_WIDGET_MODAL, editData);
			} else if (editData.type === WIDGET_TYPE.highlight_lob_banner) {
				this.onClickOpenModal(REFS_HIGHLIGHT_LOB_WIDGET_MODAL, editData);
			} else if (editData.type === WIDGET_TYPE.lob) {
				this.onClickOpenModal(REFS_LOB_WIDGET_MODAL, editData);
			}
		},

		handleRemove() {
			this.$refs[this.widgetRefs].close();
			this.$refs['modal-remove'].open();
		},

		onClickLink(item) {
			const editData = this.widgets.find((widget) => widget.id === item.id);

			if (editData) {
				if (item.type === WIDGET_TYPE.wysiwyg) {
					//* type wysiwyg
					this.onClickOpenModal(REFS_ADD_WYSIWYG_MODAL, editData);
				} else if (item.type === WIDGET_TYPE.product) {
					if (editData.data.length) {
						//* router to marketing campaign id
						const id = editData.data[0].id;
						const routeData = this.$router.resolve({
							name: ROUTE_NAME.MARKETING_CAMPAIGN_EDIT,
							params: { id },
						});
						window.open(routeData.href, '_blank');
					} else {
						//* router to marketing campaign list
						const routeData = this.$router.resolve({ name: ROUTE_NAME.MARKETING });
						window.open(routeData.href, '_blank');
					}
				} else {
					const routeName = WIDGET_ROUTES[item.type];
					if (routeName) {
						const routeData = this.$router.resolve({
							name: routeName,
							query: item.type === WIDGET_TYPE.product_category ? {} : { page_widget_id: item.id, is_visible: 1 },
						});
						window.open(routeData.href, '_blank');
					}
				}
			}
		},

		onClickOpenModal(widgetRefs, widget = null) {
			this.editedId = widget?.id ?? null;
			this.widgetRefs = widgetRefs;
			this.$refs[this.widgetRefs].open(widget);
		},

		async onSubmitModal(data) {
			const params = {
				page_id: this.$route.params.id,
				type: data.type,
				name: data.name,
				title_en: data.titleEN ?? data.title_en,
				title_th: data.titleTH ?? data.title_th,
				selling_type: data.sellingType ?? data.selling_type ?? null,
				content_en: data.contentEn ?? data.content_en ?? '',
				content_th: data.contentTh ?? data.content_th ?? '',
				is_display_button: data.displayButton ?? data.is_display_button,
				button_name_en: data.buttonNameEN ?? data.button_name_en,
				button_name_th: data.buttonNameTH ?? data.button_name_th,
				url_link: data.widgetLink ?? data.link, // Handle both modal formats
				is_active: data.websiteVisibility?.value === 'on' ? 1 : 0,
				is_mobile_active: data.mobileVisibility?.value === 'on' ? 1 : 0,
				short_description: data.short_description ?? data.shortDescription,
				side_toggle_type: data.side_toggle_type ?? data.sideToggleType,
				display_page: data.display_page ?? data.displayPage,
			};

			let isSuccess = false;
			const id = this.editedId;
			if (id) {
				await this.updatePageWidgets({ id, params });
				if (this.editWidgetState.isSuccess) {
					isSuccess = true;
				}
			} else {
				await this.createPageWidgets(params);
				if (this.createWidgetState.isSuccess) {
					isSuccess = true;
				}
			}

			if (isSuccess) {
				this.$refs[this.widgetRefs].close();
				await this.fetchWidgets();
			}
		},
	},
};
</script>

<style lang="scss" scoped>
	.btn-search {
		background: transparent;
		box-shadow: none;
		border: none;
		padding: 0;
	}

	.table-container {
		::v-deep {
			.link-to {
				color: $color-primary;
				cursor: pointer;

				&:hover {
					color: $color-primary;
				}
			}

			.link-status {
				font-size: rem($font-size-caption);
				color: $color-black-45;
			}

			.table-draggable {
				tbody {
					td:first-child {
						border-left: 0;
					}

					td:last-child {
						border-right: 0;
					}
				}
			}

			.col-name {
				width: rem(250);
			}

			.col-type {
				width: rem(200);
			}

			.col-link-to {
				width: auto;
			}

			.col-edit {
				width: rem(50);
			}
		}
	}
</style>
